/**
 * Feb 2026 (Phase B) — Screen Wake Lock hook.
 *
 * Rationale: iOS Safari / iOS home-screen PWA pauses `navigator.geolocation
 * .watchPosition` whenever the page is not visible. Wake Lock keeps the
 * screen ON (and therefore the page visible) so pings keep flowing during
 * an active delivery. This is the standard rideshare pattern — drivers
 * mount the phone on the dash and never lock the screen mid-drive.
 *
 * Called from DriverDashboard whenever the driver has at least one
 * `out_for_delivery` order. Released the moment they don't.
 *
 * Limitations (documented for the pilot team):
 *   - iOS drops the wake lock on backgrounding; we re-request on
 *     `visibilitychange → visible` automatically.
 *   - Screen may still dim/brighten per OS setting — that's fine, the
 *     runtime keeps ticking.
 *   - Browsers without the Wake Lock API (older iOS <16.4, some Firefox
 *     variants) silently no-op. Drivers there fall back to leaving the
 *     screen on manually — no worse than before.
 */
import { useEffect, useRef, useCallback } from 'react';

export function useWakeLock(active) {
  const sentinelRef = useRef(null);

  const request = useCallback(async () => {
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    // Already held.
    if (sentinelRef.current) return;
    try {
      const sentinel = await navigator.wakeLock.request('screen');
      sentinelRef.current = sentinel;
      // OS releases the lock on background — clear our ref so the next
      // foreground event can re-request cleanly.
      sentinel.addEventListener('release', () => {
        if (sentinelRef.current === sentinel) sentinelRef.current = null;
      });
    } catch (_) {
      // Denied or unsupported — silent by design.
    }
  }, []);

  const release = useCallback(async () => {
    const sentinel = sentinelRef.current;
    sentinelRef.current = null;
    if (!sentinel) return;
    try { await sentinel.release(); } catch (_) { /* noop */ }
  }, []);

  // Acquire/release based on the `active` flag.
  useEffect(() => {
    if (active) request();
    else release();
  }, [active, request, release]);

  // Re-request on foreground return — iOS drops the lock on background.
  useEffect(() => {
    if (!active) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') request();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [active, request]);

  // Belt-and-braces: release on unmount so we never leak a lock.
  useEffect(() => release, [release]);

  return { held: !!sentinelRef.current, request, release };
}
